import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useRouteError } from 'react-router';

// Flexar: auto-recover from stale lazy-chunk errors. A lazy route's dynamic import
// rejects when its chunk is stale — e.g. after a redeploy (the hashed file the open
// tab references is gone) or, in dev, after Vite rebuilds while the tab is open. Match
// the dynamic-import / preload failure
// messages across engines (Chrome/Firefox share "dynamically imported module",
// Safari uses "module script failed", Vite's CSS preload throws "Unable to preload
// CSS"). Kept narrow so a real render/API error is NOT taken for a chunk error —
// that would reload-loop instead of surfacing the bug.
const CHUNK_ERROR_RE =
  /dynamically imported module|module script failed|Loading chunk\b|ChunkLoadError|Unable to preload CSS/i;

// One auto-reload per tab session. A monotonic counter (not a wall-clock window)
// is immune to the slow-boot race: if booting the reloaded page back to the failing
// import takes longer than any timeout, a counter still won't grant a second reload.
const RELOAD_GUARD_KEY = 'flexar:chunk-reload-count';
const MAX_AUTO_RELOADS = 1;

function errorText(err: unknown): string {
  if (!err) return '';
  if (err instanceof Error) return `${err.name}: ${err.message}`;
  if (typeof err === 'object' && err !== null && 'message' in err) {
    return String((err as { message?: unknown }).message);
  }
  return String(err);
}

function isChunkLoadError(err: unknown): boolean {
  return CHUNK_ERROR_RE.test(errorText(err));
}

// Peek (no write) — may we still auto-reload? Fails closed: if the store can't be
// read, we don't auto-reload (better a manual screen than an unguarded loop).
function canAutoReload(): boolean {
  try {
    return (
      (Number(sessionStorage.getItem(RELOAD_GUARD_KEY)) || 0) < MAX_AUTO_RELOADS
    );
  } catch {
    return false;
  }
}

// Record one attempt and confirm it actually persisted. Returns false (→ no reload)
// if the store can't be read/written or the write didn't stick (Safari private mode,
// quota) — so a non-persisting store can never produce an unbounded loop.
function recordReloadAttempt(): boolean {
  let n = 0;
  try {
    n = Number(sessionStorage.getItem(RELOAD_GUARD_KEY)) || 0;
  } catch {
    return false;
  }
  if (n >= MAX_AUTO_RELOADS) return false;
  try {
    sessionStorage.setItem(RELOAD_GUARD_KEY, String(n + 1));
    return (Number(sessionStorage.getItem(RELOAD_GUARD_KEY)) || 0) > n;
  } catch {
    return false;
  }
}

const FallbackComponent: React.FC = () => {
  // FallbackComponent is wired only as a React Router `errorElement`, so the error
  // comes from useRouteError() (which throws if used outside a data router).
  const error = useRouteError();
  const { t } = useTranslation();

  const [reloading, setReloading] = useState(
    () => isChunkLoadError(error) && canAutoReload(),
  );
  const didReload = useRef(false);

  useEffect(() => {
    if (!reloading || didReload.current) return;
    didReload.current = true;
    // Side effect runs after commit, so the "updating" notice paints first.
    if (recordReloadAttempt()) {
      window.location.reload();
    } else {
      // Couldn't record the attempt — don't risk a loop; show the manual screen.
      setReloading(false);
    }
  }, [reloading]);

  if (reloading) {
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        <p>{t('error_boundary.updating', 'Updating the app…')}</p>
      </div>
    );
  }

  return (
    <div style={{ padding: '20px', textAlign: 'center' }}>
      <h2>{t('error_boundary.title', 'Something went wrong')}</h2>
      <p>
        {t(
          'error_boundary.description',
          'Sorry, an error occurred while loading the page.',
        )}
      </p>
      {!!error && (
        <details style={{ whiteSpace: 'pre-wrap', marginTop: '16px' }}>
          <summary>{t('error_boundary.details', 'Error details')}</summary>
          {errorText(error)}
        </details>
      )}
      <div style={{ marginTop: '16px' }}>
        <button
          onClick={() => window.location.reload()}
          style={{
            padding: '8px 16px',
            backgroundColor: '#1890ff',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
          }}
        >
          {t('error_boundary.reload', 'Reload Page')}
        </button>
      </div>
    </div>
  );
};

export default FallbackComponent;
